import { useEffect, useState } from "react";
import { ExternalLink, KeyRound, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PackageSelect, Problem, detail, danger, field, ghost, primary } from "./ui";

const Secret = ({ s, close }) => (
  <div data-testid="mail-password-once" className="mt-4 rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-4 text-sm text-emerald-100">
    <p className="font-semibold">{s.address} — password (shown once, not stored):</p>
    <code data-testid="mail-password-value" className="mt-2 block select-all rounded bg-black/40 px-3 py-2 font-mono">{s.password}</code>
    <button onClick={close} data-testid="mail-password-dismiss" className={`${ghost} mt-3`}>I've saved it</button>
  </div>
);

export const MailPanel = ({ pkgs }) => {
  const [pid, setPid] = useState("");
  const [data, setData] = useState(null);
  const [local, setLocal] = useState("");
  const [secret, setSecret] = useState(null);
  const [delId, setDelId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fail, setFail] = useState(null);
  const load = (id = pid) => id && api.get(`/admin/twentyi/mail/${id}`).then((r) => setData(r.data)).catch((e) => setFail(detail(e)));
  useEffect(() => { setData(null); setFail(null); setSecret(null); load(pid); }, [pid]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (fn) => {
    setBusy(true); setFail(null);
    try { await fn(); load(); } catch (e) { setFail(detail(e)); }
    setBusy(false);
  };
  const create = () => act(async () => { const r = await api.post(`/admin/twentyi/mail/${pid}`, { local }); setSecret(r.data); setLocal(""); toast.success("Mailbox created"); });
  const reset = (b) => act(async () => { const r = await api.post(`/admin/twentyi/mail/${pid}/${b.id}/reset`); setSecret({ ...r.data, address: b.address }); });
  const remove = (b) => act(async () => { await api.delete(`/admin/twentyi/mail/${pid}/${b.id}`); setDelId(null); toast.success(`${b.address} deleted`); });
  return (
    <div data-testid="mail-panel">
      <PackageSelect pkgs={pkgs} value={pid} onChange={setPid} testId="mail-package-select" />
      <Problem d={fail} testId="mail-error" />
      {secret && <Secret s={secret} close={() => setSecret(null)} />}
      {data && (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-slate-300">
            <span data-testid="mail-usage">{data.usage.used} mailbox(es) · allowance {String(data.usage.allowance)}</span>
            <a href={data.webmail} target="_blank" rel="noopener noreferrer" data-testid="mail-webmail-link" className="inline-flex items-center gap-1 text-cyan-200 underline">Webmail <ExternalLink className="h-3.5 w-3.5" /></a>
          </div>
          <ul data-testid="mail-list" className="mt-3 space-y-2">
            {data.mailboxes.map((b) => (
              <li key={b.id} data-testid={`mail-row-${b.id}`} className="flex flex-wrap items-center gap-3 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2 text-sm text-white">
                <span className="flex-1">{b.address}</span>
                <span className="text-xs text-slate-500">{b.usage_mb ?? 0} / {b.quota_mb ?? "—"} MB</span>
                <button onClick={() => reset(b)} disabled={busy} data-testid={`mail-reset-${b.id}`} className="inline-flex items-center gap-1 text-cyan-200"><KeyRound className="h-4 w-4" />Reset password</button>
                {delId === b.id ? (
                  <span className="flex gap-2"><button onClick={() => remove(b)} disabled={busy} data-testid={`mail-delete-confirm-${b.id}`} className={danger}>Delete {b.address}</button>
                    <button onClick={() => setDelId(null)} data-testid={`mail-delete-cancel-${b.id}`} className={ghost}>Cancel</button></span>
                ) : <button onClick={() => setDelId(b.id)} aria-label="Delete mailbox" data-testid={`mail-delete-${b.id}`} className="text-red-300"><Trash2 className="h-4 w-4" /></button>}
              </li>
            ))}
            {!data.mailboxes.length && <li className="text-sm text-slate-500">No mailboxes yet.</li>}
          </ul>
          {!data.protected && (
            <div className="mt-4 flex gap-2">
              <div className="flex flex-1 items-center rounded-lg border border-white/10 bg-[#07090E] pr-3">
                <input value={local} onChange={(e) => setLocal(e.target.value.toLowerCase())} placeholder="name" data-testid="mail-local-input" className="w-full bg-transparent px-3 py-2 text-sm text-white outline-none" />
                <span className="whitespace-nowrap text-sm text-slate-500">@{data.domain}</span>
              </div>
              <button onClick={create} disabled={busy || !local} data-testid="mail-create-btn" className={primary}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Create</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
